import React from 'react';

import { PAYWALL_NOTES } from '@/constants/campaign-notes';
import { isRebillStripePlanId } from '@/constants/plans';
import { webCampaign } from '@/modules/webCampaign';
import { StripeV2SubscriptionPrice } from '@/services/stripe/dto';

import { MonthlyRebill7999DescriptionAgreement } from './Agreement/MonthlyRebill7999DescriptionAgreement';
import { MonthlyRebillDescriptionAgreement } from './Agreement/MonthlyRebillDescriptionAgreement';
import { ThreeMonthsRebillDescriptionAgreement } from './Agreement/ThreeMonthsRebillDescriptionAgreement';
import { WeeklyDescriptionAgreement } from './Agreement/WeeklyDescriptionAgreement';
import { WeeklyRebillDescriptionAgreement } from './Agreement/WeeklyRebillDescriptionAgreement';
import { YearlyRebillDescriptionAgreement } from './Agreement/YearlyRebillDescriptionAgreement';
import { DefaultDescription } from './DefaultDescription';
import { MonthlyRebill7999Description } from './MonthlyRebill7999Description';
import { MonthlyRebillDescription } from './MonthlyRebillDescription';
import { ThreeMonthsRebillDescription } from './ThreeMonthsRebillDescription';
import { WeeklyDescription } from './WeeklyDescription';
import { WeeklyRebillDescription } from './WeeklyRebillDescription';
import { YearlyRebillDescription } from './YearlyRebillDescription';

export const PaymentDescriptionTermsAndLinks: React.FC = () => {
  const termsLink = webCampaign.getLegalTermsLink();
  const email = webCampaign.getLegalSupportEmail();

  return (
    <>
      To learn more, visit our{' '}
      <a href={termsLink} target="_blank">
        Terms of Use
      </a>{' '}
      or contact us via email{' '}
      <a href={`mailto:${email}`} target="_blank">
        {email}
      </a>
    </>
  );
};

const PaymentDescriptionWithAgreement: React.FC<{
  selectedPlan: StripeV2SubscriptionPrice;
  monthlyPlan: StripeV2SubscriptionPrice | undefined | null;
}> = ({ selectedPlan, monthlyPlan }) => {
  switch (selectedPlan.id) {
    case 'pro.rebill_1w_discount':
      if (monthlyPlan) {
        return (
          <WeeklyRebillDescriptionAgreement
            selectedPlan={selectedPlan}
            monthlyPlan={monthlyPlan}
          />
        );
      }
      break;
    case 'pro.rebill_1w_discount_8_99':
    case 'pro.rebill_1w_discount_9_99':
    case 'pro.rebill_1w_discount_11_99':
    case 'pro.rebill_week_19_99':
    case 'pro.rebill_week_9_99_to_21_99':
      return <WeeklyDescriptionAgreement selectedPlan={selectedPlan} />;
    case 'pro.rebill_1m_discount':
    case 'pro.rebill_month_29_99':
    case 'pro.rebill_month_29_99_to_39_99':
    case 'pro.rebill_month_49_99':
      return <MonthlyRebillDescriptionAgreement selectedPlan={selectedPlan} />;
    case 'pro.rebill_2weeks_39_99':
      return (
        <MonthlyRebill7999DescriptionAgreement selectedPlan={selectedPlan} />
      );
    case 'pro.rebill_3m_discount':
    case 'pro.rebill_3months_59_99':
    case 'pro.rebill_3months_59_99_to_79_99':
      return (
        <ThreeMonthsRebillDescriptionAgreement selectedPlan={selectedPlan} />
      );
    case 'pro.rebill_year_79_99_to_159_99':
      return <YearlyRebillDescriptionAgreement selectedPlan={selectedPlan} />;
    default:
      return <DefaultDescription />;
  }

  return <DefaultDescription />;
};

export const PaymentDescription: React.FC<{
  selectedPlan: StripeV2SubscriptionPrice | undefined | null;
  monthlyPlan: StripeV2SubscriptionPrice | undefined | null;
}> = ({ selectedPlan, monthlyPlan }) => {
  const requireTermsCheckbox = webCampaign.setup.paywallNotes.includes(
    PAYWALL_NOTES.REQUIRE_TERMS_CHECKBOX,
  );

  if (requireTermsCheckbox && selectedPlan) {
    return (
      <PaymentDescriptionWithAgreement
        selectedPlan={selectedPlan}
        monthlyPlan={monthlyPlan}
      />
    );
  }

  if (selectedPlan && isRebillStripePlanId(selectedPlan.id)) {
    switch (selectedPlan.id) {
      case 'pro.rebill_1w_discount':
        if (monthlyPlan) {
          return (
            <WeeklyRebillDescription
              selectedPlan={selectedPlan}
              monthlyPlan={monthlyPlan}
            />
          );
        }
        break;
      case 'pro.rebill_1w_discount_8_99':
      case 'pro.rebill_1w_discount_9_99':
      case 'pro.rebill_1w_discount_11_99':
      case 'pro.rebill_week_19_99':
      case 'pro.rebill_week_9_99_to_21_99':
      case 'pro.rebill_week_14_99_to_29_99':
        return <WeeklyDescription selectedPlan={selectedPlan} />;
      case 'pro.rebill_1m_discount':
      case 'pro.rebill_month_29_99':
      case 'pro.rebill_month_29_99_to_39_99':
      case 'pro.rebill_month_49_99':
        return <MonthlyRebillDescription selectedPlan={selectedPlan} />;
      case 'pro.rebill_2weeks_39_99':
        return <MonthlyRebill7999Description selectedPlan={selectedPlan} />;
      case 'pro.rebill_3m_discount':
      case 'pro.rebill_3months_59_99':
      case 'pro.rebill_3months_59_99_to_79_99':
        return <ThreeMonthsRebillDescription selectedPlan={selectedPlan} />;
      case 'pro.rebill_year_79_99_to_159_99':
        return <YearlyRebillDescription selectedPlan={selectedPlan} />;
    }
  }

  return <DefaultDescription />;
};
